import _ from 'lodash';
import { EXCLUDED_VALUE } from 'business/constants';
import { NodeIdT, SelectionBaseT, SelectionTreeT } from 'business/definitions';
import {
    addElementsToSelection,
    getFlatTreeWithId,
    getNodeAndItsDescendantsIds,
    removeElementsFromSelection,
} from 'ui/Tree/helpers';
import {
    reducedNodeIdToSelectionTree,
    selectionTreeToReducedNodeId,
} from 'components/inputs/TreeTablePicker/helpers';

export type GetNodeIdT = (arg1: TreeT) => NodeIdT;
type GetNodeIdTOrUndefined = (arg1: TreeT) => NodeIdT | undefined;
export type ReducedNodeIdT = {
    nodeId: NodeIdT;
    isSubtreeSelected: boolean;
};
type MappingT = Record<NodeIdT, NodeIdT>;

/** Return an iterator that goes through the tree in a depth first manner.
 * Include the given node in the iteration.
 * */
function* depthFirstGen<T extends TreeT>(tree: T): IterableIterator<T> {
    if (tree === null) return;
    const stack: Array<TreeT> = [tree];
    while (stack.length > 0) {
        const node = stack.pop() as T;
        yield node;

        if (node!.children.length > 0) {
            for (let i = node.children.length - 1; i >= 0; i--) {
                stack.push(node.children[i]);
            }
        }
    }
}

export function depthFirst<T extends TreeT>(tree: T): Array<T> {
    return Array.from<T>(depthFirstGen(tree));
}

const getAllDescendantsId = (tree: TreeT, getNodeId: GetNodeIdT): NodeIdT[] =>
    depthFirst(tree).map(getNodeId);

/*
  Return the sub tree with the node at his head.
  Return null if not found.
*/
export const getNode = (
    tree: TreeT,
    nodeId: NodeIdT,
    getNodeId: GetNodeIdT = (node) => node.id,
    getNodeIdAlternative: GetNodeIdTOrUndefined = (node) => node.key,
): TreeT => {
    for (const leaf of depthFirstGen(tree)) {
        if (getNodeId(leaf) === nodeId) return leaf;
        if (getNodeIdAlternative(leaf) === nodeId) return leaf;
    }
    throw new Error('Unknown tree');
};

export const getParent = (
    tree: TreeT,
    nodeId: NodeIdT,
    getNodeId: GetNodeIdT = (node) => node.id,
): TreeT | null => {
    for (const leaf of depthFirstGen(tree)) {
        if (leaf.children.some((child) => getNodeId(child) === nodeId))
            return leaf;
    }
    return null;
};

/* Simplify a tree selection, keeping only the subtree heads for nodes
 * whose subtree are fully selected.
 *
 * NOTA BENE 1:
 * `isSubtreeSelected` means that we can simplify the selection because the node subtree is included
 * For the leaves, isSubtreeSelected is set to true (so that potential future children nodes
 * will be included)
 *
 * NOTA BENE 2:
 * We need `areAllDescendentsSelected` and `isSubtreeSelected`, because areAllDescendentsSelected
 * includes the information about the nodes that are not selected, whether isSubtreeSelected
 * is only present for node selected - hence we cannot merge the two booleans.
 */

export function minifySelectionIteration(
    tree: TreeT,
    selection: NodeIdT[],
    getNodeId: GetNodeIdT,
): {
    areAllDescendentsSelected: boolean;
    selectedNodes: ReducedNodeIdT[];
} {
    const childrenIteration = tree.children.map((child) =>
        minifySelectionIteration(child, selection, getNodeId),
    );

    const isSelected = selection.includes(getNodeId(tree)); // use getNodeId
    const areAllDescendentsSelected = childrenIteration.every(
        (childIter) => childIter.areAllDescendentsSelected,
    );

    const selectedNodes = _(childrenIteration)
        .map((childIter) => childIter.selectedNodes)
        .flatten()
        .value();

    if (isSelected) {
        if (areAllDescendentsSelected) {
            return {
                // we replace all the subtree with the its head: the current node
                selectedNodes: [
                    { nodeId: getNodeId(tree), isSubtreeSelected: true },
                ],
                areAllDescendentsSelected: true,
            };
        }
        // NB: the order is important in the tests
        return {
            selectedNodes: [
                { nodeId: getNodeId(tree), isSubtreeSelected: false },
                ...selectedNodes,
            ],
            areAllDescendentsSelected: false,
        };
    }
    return {
        selectedNodes,
        areAllDescendentsSelected: false,
    };
}

/* Transform a list of selected items in the TreeTableGrid ( depends on what renders getNodeId ) into an array of object like
{nodeId, isSubtreeSelected} */
export function reduceSelection(
    tree: TreeT,
    selection: NodeIdT[],
    getNodeId: GetNodeIdT,
): ReducedNodeIdT[] {
    const { selectedNodes } = minifySelectionIteration(
        tree,
        selection,
        getNodeId,
    );
    return selectedNodes;
}

const getSubtreeIds = (
    tree: TreeT,
    nodeId: NodeIdT,
    isSubtreeSelected: boolean,
    getNodeId: GetNodeIdT,
): NodeIdT[] => {
    try {
        const node = getNode(tree, nodeId, getNodeId);
        if (isSubtreeSelected) return getAllDescendantsId(node, getNodeId);
        return [nodeId];
    } catch {
        return [];
    }
};

/* Transform an array of object {nodeId, isSubtreeSelected} into a list of selected nodes. */
export function unminifySelection(
    tree: TreeT,
    selectionWithSubtree: ReducedNodeIdT[],
    getNodeId: GetNodeIdT,
): NodeIdT[] {
    return _(selectionWithSubtree)
        .map(({ nodeId, isSubtreeSelected }) =>
            getSubtreeIds(tree, nodeId, isSubtreeSelected, getNodeId),
        )
        .flatten()
        .uniq()
        .value();
}

/** Return a new tree and filter out the nodes using the given filter function.
 *  This is like Array.filter but for tree.
 */
export function filterTree<T extends TreeT>(
    tree: T,
    filter: (arg1: T) => boolean,
): T {
    const newChildren = tree.children

        .filter(filter)
        .map((child) => filterTree(child, filter));
    return { ...tree, children: newChildren };
}

export function filterExcludedValue<T extends TreeT>(tree: T): T {
    return filterTree(tree, (node) => node.value !== EXCLUDED_VALUE);
}

export function copyTree<T extends TreeT>(tree: T): T {
    return _.cloneDeep(tree);
}

/** Sort in place a tree: by value for the ones with children, then by value for the ones without */
export function sortTreeByChildren<T extends TreeT>(tree: T): T {
    tree.children.sort((a, b) => {
        if (a.children.length > 0 && b.children.length === 0) {
            return -1;
        }
        if (a.children.length === 0 && b.children.length > 0) {
            return 1;
        }
        if (a.value === null) {
            // comparison cannot be done with a null element
            return -1;
        }
        return a.value.localeCompare(b.value);
    });
    tree.children.forEach((node) => sortTreeByChildren(node));
    return tree;
}

export function sortTreeByChildrenLabel<T extends TreeT>(
    tree: T,
    sortSubTreesFirst: boolean = true,
): T {
    tree.children.sort((a, b) => {
        // place all subtrees at the beginning of the children list
        if (sortSubTreesFirst) {
            if (a.children.length > 0 && b.children.length === 0) {
                return -1;
            }
            if (a.children.length === 0 && b.children.length > 0) {
                return 1;
            }
        }
        if (a.value === null) {
            // comparison cannot be done with a null element
            return -1;
        }
        return a.label.localeCompare(b.label);
    });
    tree.children.forEach((node) =>
        sortTreeByChildrenLabel(node, sortSubTreesFirst),
    );
    return tree;
}

export const getAncestorsIds = (childId: NodeIdT, map: MappingT): NodeIdT[] => {
    // @ts-expect-error [TS migration] (previously $FlowFixMe)
    const parentId = map.get(childId);
    return parentId !== undefined
        ? [parentId, ...getAncestorsIds(parentId, map)]
        : [];
};

export const getChildParentMapping = (
    tree: TreeT,
    getNodeId: GetNodeIdT,
): MappingT => {
    const mapping = new Map();
    depthFirst(tree).forEach((leaf) => {
        leaf.children.forEach((child) =>
            mapping.set(getNodeId(child), getNodeId(leaf)),
        );
    });
    // @ts-expect-error [TS migration] (previously $FlowFixMe) flow upgrade: to investigate
    return mapping;
};

export const getExpandedRow = (
    selectedNodeIds: NodeIdT[],
    tree: TreeT,
    getNodeId: GetNodeIdT,
): NodeIdT[] => {
    const childParentMap = getChildParentMapping(tree, getNodeId);
    return _(selectedNodeIds)
        .map((selected) => getAncestorsIds(selected, childParentMap))
        .flatten()
        .uniq()
        .value();
};

export const depth = (tree: TreeT) => {
    if (tree.children.length === 0) {
        return 0;
    }

    let maxDepth = 0;
    for (const child of tree.children) {
        maxDepth = Math.max(maxDepth, depth(child));
    }
    return 1 + maxDepth;
};
/**
 * Find the nodeId in a tree and return the node subtree
 * @example
 * Input :
 *  - nodeId: 2
 *  - tree :
 *     0
 *     |- 1
 *     |- 2
 *     |  |- 21
 *     |  |- 22
 *     |  |   |- 221
 *     |- 3
 *
 * Output :
 *     2
 *     |- 21
 *     |- 22
 *     |   |- 221
 *
 */
export const getNodeSubTree = (
    tree: TreeT,
    nodeId: number,
): TreeT | undefined => {
    if (tree.id === nodeId) return tree;

    for (let i = 0; i < tree.children.length; i += 1) {
        const subtree = getNodeSubTree(tree.children[i], nodeId);
        if (subtree) {
            return subtree;
        }
    }

    return undefined;
};

export const selectTreeNode = (
    tree: TreeT,
    node: TreeT,
    setSelectedNodeIds: React.Dispatch<React.SetStateAction<number[]>>,
): void => {
    const nodeSubTree = getNodeSubTree(tree, node.id);
    if (nodeSubTree === undefined)
        throw Error(`No subTree find with node id : ${node.id}`);

    const nodeAndItsDescendantsIds = getNodeAndItsDescendantsIds(nodeSubTree);

    setSelectedNodeIds((prevSelection) => {
        if (prevSelection.includes(node.id)) {
            return removeElementsFromSelection(
                prevSelection,
                nodeAndItsDescendantsIds,
            );
        }

        return addElementsToSelection(prevSelection, nodeAndItsDescendantsIds);
    });
};

const getNodeValue = (node: TreeT) => node.value;
const getNodeId = (node: TreeT) => node.id;

export const getNodeIdsFromValues = (
    tree: TreeT,
    nodeValues: string[],
): number[] => {
    const flatTree = getFlatTreeWithId(tree);
    return nodeValues.map((value) => {
        const node = flatTree.find((n) => n.value === value);
        if (node === undefined)
            throw new Error(`Node with value ${value} not found`);
        return node.id;
    });
};

export const getExpandedRowIds = (
    tree: TreeT,
    selection: SelectionBaseT[],
): string[] => {
    const expandedRows = getExpandedRow(
        selection.map((s) => s.value),
        tree,
        getNodeValue,
    ) as string[];

    return getNodeIdsFromValues(tree, expandedRows).map((id) => String(id));
};

export const getSelectionTree = (
    tree: TreeT,
    selectedNodeIds: number[],
): SelectionTreeT[] => {
    const nodeIdsWithIsSubtreeField = reduceSelection(
        tree,
        selectedNodeIds,
        getNodeId,
    );

    const selectionTree = nodeIdsWithIsSubtreeField.map((el) =>
        reducedNodeIdToSelectionTree(el, tree, getNodeId),
    );

    return selectionTree;
};

export const getNodeIdsFromSelection = (
    tree: TreeT,
    selection: SelectionBaseT[],
) => {
    const nodeValues = unminifySelection(
        tree,
        selection.map(selectionTreeToReducedNodeId),
        getNodeValue,
    ) as string[];
    return getNodeIdsFromValues(tree, nodeValues);
};

export const isFlatTree = (tree: TreeT): boolean => depth(tree) <= 1;

export const getFlatTreeNodeIdsFromSelection = (
    tree: TreeT,
    selection: SelectionBaseT[],
): number[] => {
    const selectionValues: string[] = selection.map((s) => s.value);
    return tree.children
        .filter((node) => selectionValues.includes(node.value))
        .map((node) => node.id);
};

export const getSelectionTreeFromFlatTree = (
    tree: TreeT,
    selectedNodeIds: number[],
): SelectionTreeT[] =>
    tree.children
        .filter((node) => selectedNodeIds.includes(node.id))
        .map((node) => ({
            label: node.label,
            value: node.value,
            isBadValue: false,
            isSubtreeSelected: false,
        }));
